import { ref, computed, watch, Ref } from 'vue';
import { LatLng, Point } from 'leaflet';
import StationStore from '@/stores/station/station-module';
import MapStore from '@/stores/map/map-module';
import { ToolPurpose } from '@/stores/map/map-model';
import { colors } from '@/plugins/vuetify';
import { Station } from '@/stores/station/station-model';

export function useStationSelector(
  props: {
    startAnchor: LatLng;
    endAnchor: LatLng;
    finished: boolean;
    purpose: ToolPurpose;
  },
  stationsInSelection: Ref<Station[]>,
  t: (key: string) => string
) {
  const stationStore = StationStore;
  const mapStore = MapStore;

  const startAnchor = ref(props.startAnchor);
  const endAnchor = ref(props.endAnchor);
  const finished = ref(props.finished);
  const purpose = ref(props.purpose);

  const color = colors.primary;

  const cursorIsRightOfStart = computed(() => {
    return startAnchor.value.lng > endAnchor.value.lng;
  });

  const tooltipPosition = computed<string>(() => {
    if (cursorIsRightOfStart.value) return 'left';
    return 'right';
  });

  const tooltipOffset = computed<Point>(() => {
    if (cursorIsRightOfStart.value) return new Point(-5, 0);
    return new Point(5, 0);
  });

  const numOfAffectedStationsTooltip = computed<string>(() => {
    let num = 0;
    let verb = '';
    if (props.purpose === ToolPurpose.add) {
      num = stationsInSelection.value.filter(
        (station) => !stationStore.selectedStationsIds.has(station.id)
      ).length;
      verb = t('station_map.add').toString();
    } else if (props.purpose === ToolPurpose.remove) {
      num = stationsInSelection.value.filter((station) =>
        stationStore.selectedStationsIds.has(station.id)
      ).length;
      verb = t('station_map.remove').toString();
    } else if (props.purpose === ToolPurpose.replace) {
      num = stationsInSelection.value.length;
      verb = t('station_map.replace').toString();
    }
    const stationWord =
      num === 1
        ? t('station_map.station')
        : t('station_map.stations');
    return `${num} ${stationWord} ${verb}`;
  });

  function handleFinished() {
    if (props.purpose === ToolPurpose.remove) {
      stationStore.removeStationsFromSelected(stationsInSelection.value);
    } else if (props.purpose === ToolPurpose.add) {
      stationStore.addStationsToSelected(stationsInSelection.value);
    } else {
      stationStore.setSelectedStations(stationsInSelection.value);
    }
  }
  
  function handleInput() {
    mapStore.updateMarkedStations(stationsInSelection.value);
  }

  watch(endAnchor, () => {
    handleInput();
  });

  watch(finished, () => {
    if (finished.value) {
      handleFinished();
    }
  });

  watch(() => props.startAnchor, v => (startAnchor.value = v));
  watch(() => props.endAnchor, v => (endAnchor.value = v));
  watch(() => props.finished, v => (finished.value = v));
  watch(() => props.purpose, v => (purpose.value = v));

  return {
    startAnchor,
    endAnchor,
    finished,
    purpose,
    color,
    stationsInSelection,
    cursorIsRightOfStart,
    tooltipPosition,
    tooltipOffset,
    numOfAffectedStationsTooltip,
    handleInput,
    handleFinished,
    stationStore,
    mapStore,
  };
}